import React from 'react';
import type { ComponentType, ClassAttributes, HTMLAttributes, ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { styled, type StyledProps } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import type { HubAppProps } from '../../types';

// When the space-density experience is active, HubSpacingScope / hub-home-content
// set `--ak-editor-extension-block-spacing` on an ancestor. The wrapper reads
// that variable (falling back to `0`) as top/bottom PADDING on each bespoke hub
// extension, so the vertical rhythm between extensions follows the selected
// density. Padding (not margin) is used deliberately so adjacent extensions do
// not margin-collapse — the gap between two extensions is the sum of the lower
// extension's top padding and the upper extension's bottom padding. When the
// variable is unset the padding is `0`, so this is a no-op for hubs without a
// density selected.
const extensionBlockPadding = 'var(--ak-editor-extension-block-spacing, 0)';

// This is a copy of confluence/next/packages/company-hub/src/hub-layout/HubRendererWrapper.tsx
// For now we should keep them in sync until Confluence is fully migrated to hub-renderer
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
export const HubRendererWrapper: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
> = styled.div({
	// Include the extension's 48px gutters in both the canvas cap and available width.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&[data-hub-max-width]': {
		'--hub-renderer-default-extension-max-width': 'min(704px, calc(100% - 96px))',
		'--hub-renderer-wide-extension-max-width': 'min(955px, calc(100% - 96px))',
		'--hub-renderer-full-width-extension-max-width': 'min(3904px, calc(100% - 96px))',
	},
	// removing 96 gutter (akEditorBreakoutPadding) width by appling negative margin
	// this increases the width of extension by 96px
	// we have to adjust this value
	// in Editor we adds 40px in the same way
	// to compensate the difference we limit the width of extension to
	// 704px - default (800 - 96)
	// 955px - wide (1051 - 96)
	// 1744px - full width (1840 - 96)
	// Include the renderer's centering wrapper for max only; keep legacy selectors unchanged.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .ak-renderer-document, & [data-multibodiedextension-container], &[data-hub-max-width] .ak-renderer-sticky-safe-center-wrapper':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension': {
				// Horizontal gutter (unchanged). Vertical padding follows the selected
				// space density (0 when unset — a no-op for hubs without a density).
				paddingTop: extensionBlockPadding,
				paddingBottom: extensionBlockPadding,
				paddingLeft: token('space.600', '48px'),
				paddingRight: token('space.600', '48px'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'& > div.ak-renderer-extension-overflow-container': {
					margin: '0 -48px',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=default]': {
				// extensions with default layout should have 800px max-width
				// for screens larger than 736px we limit the max-width to 704px -> 704+48*2 = 800
				// Legacy appearance compensates for the renderer's 64px page padding.
				// Max appearance has no document padding; reserve both 48px extension gutters instead.
				maxWidth: 'var(--hub-renderer-default-extension-max-width, min(704px, calc(100% - 32px)))',
				// positioning extension with default layout in the center
				// in the same way as we do for wide and full-width extensions
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
				marginLeft: '50%',
				transform: 'translateX(-50%)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=wide]': {
				maxWidth: 'var(--hub-renderer-wide-extension-max-width, 955px)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=full-width]': {
				maxWidth: 'var(--hub-renderer-full-width-extension-max-width, 1744px)',
			},
		},

	// Removing the margin between MBEs (Sections and any other future MBE extensions)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},

	// Removing the margin between MBE and any extension
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + div.ak-renderer-extension': {
		marginTop: 0,
	},

	// Removing the margin between any extension and MBE
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},

	// Removing the margin between any extensions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + div.ak-renderer-extension': {
		marginTop: 0,
	},
});

const HubRendererWrapperWithPadding: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
> = styled.div({
	paddingInlineStart: token('space.300'),
	paddingInlineEnd: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&[data-hub-max-width]': {
		'--hub-renderer-default-extension-max-width': 'min(704px, calc(100% - 96px))',
		'--hub-renderer-wide-extension-max-width': 'min(955px, calc(100% - 96px))',
		'--hub-renderer-full-width-extension-max-width': 'min(3904px, calc(100% - 96px))',
	},
	// Include the renderer's centering wrapper for max only; keep legacy selectors unchanged.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .ak-renderer-document, & [data-multibodiedextension-container], &[data-hub-max-width] .ak-renderer-sticky-safe-center-wrapper':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension': {
				// Horizontal gutter (unchanged). Vertical padding follows the selected
				// space density (0 when unset — a no-op for hubs without a density).
				paddingTop: extensionBlockPadding,
				paddingBottom: extensionBlockPadding,
				paddingLeft: token('space.600', '48px'),
				paddingRight: token('space.600', '48px'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'& > div.ak-renderer-extension-overflow-container': {
					margin: '0 -48px',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=default]': {
				maxWidth: 'var(--hub-renderer-default-extension-max-width, min(704px, calc(100% - 32px)))',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-space
				marginLeft: '50%',
				transform: 'translateX(-50%)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=wide]': {
				maxWidth: 'var(--hub-renderer-wide-extension-max-width, 955px)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension[data-layout=full-width]': {
				maxWidth: 'var(--hub-renderer-full-width-extension-max-width, 1744px)',
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + div.ak-renderer-extension': {
		marginTop: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + div.ak-renderer-extension': {
		marginTop: 0,
	},
});

type HubProduct = 'JSM' | 'CSM';

type HubRendererWrapperGatedProps = ClassAttributes<HTMLDivElement> &
	HTMLAttributes<HTMLDivElement> & {
		appearance?: HubAppProps['appearance'];
		children?: ReactNode;
		product?: HubProduct;
	};

export const HubRendererWrapperGated = ({
	appearance,
	children,
	product,
	...props
}: HubRendererWrapperGatedProps): React.JSX.Element => {
	const maxWidth = appearance === 'max' ? true : undefined;
	// Max-mode content keeps its page inset independently of navbar padding.
	const shouldApplyPadding =
		product === 'JSM' && (appearance === 'max' || fg('hubs_help_seeker_horizontal_padding'));
	if (shouldApplyPadding) {
		return (
			<HubRendererWrapperWithPadding {...props} data-hub-max-width={maxWidth}>
				{children}
			</HubRendererWrapperWithPadding>
		);
	}
	return (
		<HubRendererWrapper {...props} data-hub-max-width={maxWidth}>
			{children}
		</HubRendererWrapper>
	);
};
